import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/components/ArticleCard";
import { SEO } from "@/components/SEO";
import { CATEGORY_LABELS, COMMUNITY_IMAGE } from "@/lib/constants";
import { ArrowLeft, Loader2, Facebook, Twitter, MessageCircle } from "lucide-react";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/articles/${slug}`)
      .then(({ data }) => setArticle(data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading)
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="animate-spin text-brand-blue" size={32} />
      </div>
    );

  if (notFound || !article)
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Article introuvable</h1>
        <Link to="/actualites" className="mt-6 inline-flex items-center gap-2 text-brand-blue font-semibold">
          <ArrowLeft size={18} /> Retour aux actualités
        </Link>
      </div>
    );

  return (
    <article data-testid="article-detail-page">
      <SEO title={article.title} description={article.summary} image={article.image_url} type="article" />
      <div className="relative h-72 sm:h-96 bg-brand-blue">
        <img src={article.image_url} alt={article.title} className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-brand-blue/30" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 h-full flex flex-col justify-end pb-10">
          <span className="bg-brand-green text-white text-xs font-bold uppercase tracking-wide px-3 py-1.5 w-fit">
            {CATEGORY_LABELS[article.category] || article.category}
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl font-bold text-white leading-tight">{article.title}</h1>
          <p className="mt-3 text-blue-100 text-sm">
            Publié le {formatDate(article.published_at)} · {article.author}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <Link to="/actualites" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-blue mb-8">
          <ArrowLeft size={16} /> Retour aux actualités
        </Link>
        <p className="text-xl text-slate-700 font-medium leading-relaxed mb-8">{article.summary}</p>
        <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed whitespace-pre-line text-lg">
          {article.content}
        </div>

        <div className="mt-12 pt-8 border-t border-border flex items-center gap-3">
          <span className="text-sm font-semibold text-slate-700">Partager :</span>
          {[Facebook, Twitter, MessageCircle].map((Icon, i) => (
            <button key={i} className="h-10 w-10 flex items-center justify-center border border-border text-slate-600 hover:bg-brand-blue hover:text-white hover:border-brand-blue transition-colors">
              <Icon size={16} />
            </button>
          ))}
        </div>
      </div>
    </article>
  );
}
